import { lazy } from 'react';
import {
  COURSES,
  DASHBOARD,
  FULLSCRIPT_ROUTES,
  MESSAGES,
  MESSAGES_ROUTES,
  MY_ACCOUNT_PROFILE,
  MY_ACCOUNT_ROUTES,
  MY_APPOINTMENTS,
  MY_APPOINTMENT_ROUTES,
  MY_PATIENTS,
  MY_PATIENTS_ROUTES,
  MY_PROFILE_ACCOUNT_SETTINGS,
  NOTIFICATIONS,
  NOTIFICATIONS_ROUTES,
  PROVIDER_SCHEDULED,
} from 'utilities/routes';

export const loadDashboard = () =>
  import('../../app/dashboard/pages/Dashboard');
export const loadMyPatients = () =>
  import('../../app/my-patients/routes/MyPatientsRoutes');
export const loadMessages = () =>
  import('app/messages/routes/ProviderMessages');
export const loadFullscript = () =>
  import('../../app/fullscript/FullscriptRoutes');
export const loadProviderSchedules = () => import('../../app/calendar');
export const loadCourses = () => import('../../app/courses');
export const loadNotifications = () =>
  import('../../app/notifications/routes/NotificationsRoutes');
export const loadMyAppointments = () =>
  import('../../app/my-appointments/routes/MyAppointmentsRoutes');
export const loadManageTestKits = () =>
  import('../../app/manage-test-kits/routes/ManageTestKitsRoutes');

export const loadMyAccount = () =>
  import('../../app/my-account/routes/MyAccountRoutes').then((module) => ({
    default: module.MyAccountRoutes,
  }));

export const DashboardPage = lazy(loadDashboard);
export const MyPatientsRoutes = lazy(loadMyPatients);
export const MyMessagesRoutes = lazy(loadMessages);
export const FullscriptRoutes = lazy(loadFullscript);
export const ProviderSchedules = lazy(loadProviderSchedules);
export const CoursesRoutes = lazy(loadCourses);
export const NotificationsRoutes = lazy(loadNotifications);
export const MyAppointmentsRoutes = lazy(loadMyAppointments);
export const MyAccountRoutes = lazy(loadMyAccount);
export const ManageTestKitsRoutes = lazy(loadManageTestKits);

type LoaderFn = () => Promise<unknown>;

const PREFETCH_GROUPS: [Array<string | undefined>, LoaderFn][] = [
  [['dashboard', DASHBOARD], loadDashboard],
  [['my-patients', MY_PATIENTS, MY_PATIENTS_ROUTES], loadMyPatients],
  [['messages', MESSAGES, MESSAGES_ROUTES], loadMessages],
  [['fullscript', FULLSCRIPT_ROUTES], loadFullscript],
  [['calendar', 'schedule', PROVIDER_SCHEDULED], loadProviderSchedules],
  [['courses', 'education', COURSES], loadCourses],
  [['notifications', NOTIFICATIONS, NOTIFICATIONS_ROUTES], loadNotifications],
  [
    ['appointments', MY_APPOINTMENTS, MY_APPOINTMENT_ROUTES],
    loadMyAppointments,
  ],
  [
    [
      'my-account',
      MY_ACCOUNT_PROFILE,
      MY_ACCOUNT_ROUTES,
      MY_PROFILE_ACCOUNT_SETTINGS,
    ],
    loadMyAccount,
  ],
];

const PREFETCH_MAP = new Map<string, LoaderFn>();

PREFETCH_GROUPS.forEach(([triggers, load]) => {
  triggers.forEach((trigger) => {
    if (trigger) {
      PREFETCH_MAP.set(trigger.toLowerCase(), load);
    }
  });
});

export const prefetchProviderRoute = (pathOrKey?: string) => {
  if (!pathOrKey) return;

  const key = pathOrKey.toLowerCase();

  const loader = PREFETCH_MAP.get(key);
  if (loader) {
    loader();
    return;
  }

  const rootSegment = key.split('/')[1];
  if (rootSegment) {
    PREFETCH_MAP.get(rootSegment)?.();
  }
};
